import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  /** Extra classes on the dialog panel (e.g. to widen beyond size). */
  className?: string;
  /** Use when modal must appear above other overlays (e.g. z-[110] above a panel at z-[100]) */
  zIndex?: number;
  /** If true, close on Escape and trap focus inside. Default true. */
  closeOnEscape?: boolean;
}

interface ModalHeaderProps {
  children: React.ReactNode;
}

interface ModalTitleProps {
  children: React.ReactNode;
}

interface ModalContentProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  className,
  zIndex,
  closeOnEscape = true,
}) => {
  const { colors } = useTheme();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus trap: keep Tab inside modal
  useEffect(() => {
    if (!isOpen || !contentRef.current || !closeOnEscape) return;
    const el = contentRef.current;
    const focusables = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-5xl',
    /** ~10% wider than 2xl (64rem → 70.4rem) */
    '3xl': 'max-w-[70.4rem]',
  };

  const overlay = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: zIndex ?? 100 }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-zinc-950/55 backdrop-blur-[2px] transition-opacity"
        aria-hidden
        onClick={onClose}
      />
      <div
        ref={contentRef}
        className={cn(
          'relative z-10 flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border shadow-2xl shadow-black/25',
          colors.background,
          colors.border,
          sizeClasses[size],
          className,
        )}
        onClick={(e) => {
          // Stop click bubbling so overlay/parent handlers don't treat
          // inner clicks as "outside the dialog". Do not stop mousedown —
          // Selects/DateInputs inside the modal need document outside-click.
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

const ModalHeader: React.FC<ModalHeaderProps> = ({ children }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('flex shrink-0 items-center justify-between border-b p-6', colors.border)}>
      {children}
    </div>
  );
};

const ModalTitle: React.FC<ModalTitleProps> = ({ children }) => {
  const { colors } = useTheme();
  return <h2 className={cn('text-lg font-semibold', colors.text)}>{children}</h2>;
};

const ModalContent: React.FC<ModalContentProps> = ({ children, className }) => {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto p-6', className)}>{children}</div>;
};

const ModalFooter: React.FC<ModalFooterProps> = ({ children, className }) => {
  const { colors } = useTheme();
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-end gap-3 border-t p-6',
        colors.border,
        className,
      )}
    >
      {children}
    </div>
  );
};

export { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter };
